import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AutoComplete, Button, Card, Message, Spin } from "@arco-design/web-react";
import { IconLeft } from "@arco-design/web-react/icon";
import axios from "axios";
import classNames from "classnames";

import { endpoints } from "../../../api/endpoints";
import { BookingForm } from "../../../components/BookingForm/BookingForm";
import { SuccessScreen } from "../../../components/SuccessScreen/SuccessScreen";
import { UserCard } from "../../../components/UserCard/UserCard";
import { User } from "../../../models/Person";
import { routes } from "../../../routes/routes";
import { setSelectedUser, setUsersList } from "../../../store/adminSlice";
import { RootState } from "../../../store/store";
import { getCurrentDate } from "../../../utils/functions";

import './UserBooking.css';

import { currentThemeName } from "../../../theme";

const { Option } = AutoComplete;

const getFilteredProfiles = (list: User[], searchQuery: string) => {
  return list.filter((profile) => {
    const isNameMatch = searchQuery ? profile.name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    const isIdMatch = searchQuery ? profile.id.toString().includes(searchQuery.toLowerCase()) : true;
    return isNameMatch || isIdMatch;
  });
}

export function UserBooking() {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const usersList = useSelector((state: RootState) => state.admin.usersList);
  const selectedUser = useSelector((state: RootState) => state.admin.selectedUser);
  const hasUserData = selectedUser?.id.toString() === id;

  const [isLoading, setIsLoading] = useState<boolean>(!hasUserData);
  const [user, setUser] = useState<User | null>(hasUserData ? selectedUser : null);
  const [selectedDate, setSelectedDate] = useState<string>(getCurrentDate());
  const [selectedTime, setSelectedTime] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [profiles, setProfiles] = useState<User[]>(usersList);
  const [filteredProfiles, setFilteredProfiles] = useState<User[]>(getFilteredProfiles(usersList, searchQuery));
  const [selectedRespondent, setSelectedRespondent] = useState<User | null>(null);

  const [isMeetingBooked, setIsMeetingBooked] = useState<boolean>(false);

  const getProfile = () => {
    axios
      .get(`${endpoints.admin.getUser}/${id}`)
      .then((response) => {
        setUser(response.data);
        dispatch(
          setSelectedUser(response.data)
        )
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const getProfiles = () => {
    axios
      .get(`${endpoints.admin.getUsers}`)
      .then((response) => {
        setProfiles(response.data);
        setFilteredProfiles(getFilteredProfiles(response.data, searchQuery));
        dispatch(
          setUsersList(response.data)
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    getProfile();
    getProfiles();
  }, []);

  useEffect(() => {
    setFilteredProfiles(getFilteredProfiles(profiles, searchQuery))
  }, [searchQuery]);

  const bookMeeting = () => {
    if (!selectedDate) {
      Message.error(t('admin.booking.select_day'));
      return;
    }

    if (!selectedTime) {
      Message.error(t('admin.booking.select_time'));
      return;
    }

    if (!selectedRespondent) {
      Message.error(t('admin.booking.select_respondent'));
      return;
    }

    axios
      .post(`${endpoints.admin.bookMeeting}`, {
        mentor_id: user?.id,
        mentee_id: selectedRespondent.id,
        date: selectedDate,
        hour: selectedTime,
        timezone: timezone,
      })
      .then(() => {
        setIsMeetingBooked(true);
      })
      .catch(() => {
        Message.error(t('notification_messages.meeting_booked_error'));
      });
  };

  return (
    <div className={classNames("user-booking", {
      "user-booking--black-theme": currentThemeName === 'black'
    })}>
      {!isMeetingBooked && (
        <div className="user-booking__header">
          <Button
            onClick={() => {
              navigate(-1);
            }}
            className="user-booking__icon-button"
          >
            <IconLeft fontSize={24}/>
          </Button>
          <h1 className="user-booking__title">{t('admin.booking.user_profile')}</h1>
        </div>
      )}

      {!user && isLoading && (
        <div className="user-booking__loader-wrapper">
          <Spin size={40}/>
        </div>
      )}
      {!isLoading && user && !isMeetingBooked && (
        <div className="user-booking__content">
          <UserCard user={user} hideActions/>
          <Card className="user-booking__card" bordered={false}>
            <div className="user-booking__card-header">{t('admin.booking.choose_session_partner')}</div>
            <div className="user-booking__autocomplete-wrapper">
              <div className="user-booking__label">{t('admin.booking.booking_label')}</div>
              <AutoComplete
                placeholder={t('admin.booking.search')}
                onSearch={setSearchQuery}
                dropdownRender={() => {
                  return (
                    <div className="user-booking__autocomplete-dropdown">
                      {
                        filteredProfiles.map((profile) => (
                          <button onClick={() => setSelectedRespondent(profile)} key={profile.id}
                                  className="user-booking__autocomplete-option">
                            <UserCard user={profile} hideActions withShadow/>
                          </button>
                        ))
                      }
                    </div>
                  )
                }}
              >
                {
                  filteredProfiles.map((profile) => (
                    <Option className="user-booking__autocomplete-option" key={profile.id}
                            value={`${profile.name} ${profile.id}`}>
                      {profile.name} {profile.last_name}
                    </Option>
                  ))
                }
              </AutoComplete>
            </div>
            {selectedRespondent && (
              <UserCard user={selectedRespondent} hideActions/>
            )}
          </Card>
          <Card className="user-booking__card" bordered={false}>
            <BookingForm
              userId={id}
              day={selectedDate}
              time={selectedTime}
              onDayChange={setSelectedDate}
              onTimeChange={setSelectedTime}
            />
          </Card>

          <div className="user-booking__footer">
            <Button
              className="user-booking__footer-button user-booking__secondary-button"
              size="large"
              shape={"round"}
              onClick={() => {
                navigate(-1);
              }}
            >
              {t('common.cancel_button')}
            </Button>
            <Button
              className="user-booking__footer-button user-booking__primary-button"
              size="large"
              shape={"round"}
              onClick={bookMeeting}
            >
              {t('common.save_button')}
            </Button>
          </div>
        </div>
      )}
      {isMeetingBooked && (
        <SuccessScreen
          title={t('notification_messages.meeting_booked_success')}
          buttonText={t('admin.booking.back_to_profile')}
          onButtonClick={() => {
            user && navigate(routes.ADMIN_USER.replace(":id", user?.id.toString()));
          }}
        />
      )}
    </div>
  );
}